import React, { useState, useEffect, useRef } from "react";
import "./Landing.css";

type AnimationPhase =
  | "letters"
  | "diacritics"
  | "fadeOut"
  | "mSlide"
  | "mFinal"
  | "reset";

const letters = ["m", "i", "r", "s", "a", "l"];
const LAST_LETTER_INDEX = letters.length - 1;

const AnimatedMirsalText: React.FC = () => {
  const [phase, setPhase] = useState<AnimationPhase>("letters");
  const [showDiacritics, setShowDiacritics] = useState(false);
  const [showSukunOnM, setShowSukunOnM] = useState(false);
  const [wordOpacity, setWordOpacity] = useState(1);
  const [mPosition, setMPosition] = useState<"original" | "centered">(
    "original"
  );
  const [showWord, setShowWord] = useState(true);
  const [showCenteredM, setShowCenteredM] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const mRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wordRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Handle last letter animation completion
  useEffect(() => {
    if (phase !== "letters") return;

    const lastLetterElement = letterRefs.current[LAST_LETTER_INDEX];
    if (!lastLetterElement) return;

    let timeout: NodeJS.Timeout | null = null;

    const handleAnimationEnd = (e: AnimationEvent) => {
      // Only handle the letter reveal animation
      if (e.animationName === "letterReveal") {
        // Wait 500ms after last letter animation completes, then move to diacritics
        timeout = setTimeout(() => {
          setPhase("diacritics");
        }, 500);
      }
    };

    lastLetterElement.addEventListener("animationend", handleAnimationEnd);
    return () => {
      lastLetterElement.removeEventListener("animationend", handleAnimationEnd);
      if (timeout) clearTimeout(timeout);
    };
  }, [phase, animationKey]);

  // Handle word fade out transition
  useEffect(() => {
    if (phase !== "fadeOut" || wordOpacity !== 0) return;

    const wordElement = wordRef.current;
    if (!wordElement) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "opacity" && wordOpacity === 0) {
        setShowWord(false);
        setPhase("mSlide");
      }
    };

    wordElement.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      wordElement.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [phase, wordOpacity]);

  // Handle m slide transition
  useEffect(() => {
    if (phase !== "mSlide" || mPosition !== "centered") return;

    const mElement = mRef.current;
    if (!mElement) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      // Check if it's the transform transition (the slide animation)
      if (e.propertyName === "transform" || e.propertyName === "all") {
        setPhase("mFinal");
      }
    };

    mElement.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      mElement.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [phase, mPosition]);

  // Main phase handler
  useEffect(() => {
    let timeouts: NodeJS.Timeout[] = [];

    if (phase === "letters") {
      // Reset state and trigger CSS animations by changing key
      setShowDiacritics(false);
      setShowCenteredM(false);
      setShowSukunOnM(false);
      setWordOpacity(1);
      setMPosition("original");
      setShowWord(true);
      // Change animation key to restart CSS animations
      setAnimationKey((prev) => prev + 1);
    } else if (phase === "diacritics") {
      // Show diacritics after 500ms pause
      const timeout = setTimeout(() => {
        setShowDiacritics(true);
        // After diacritics shown, wait then fade out
        setTimeout(() => {
          setPhase("fadeOut");
        }, 1000);
      }, 500);
      timeouts.push(timeout);
    } else if (phase === "fadeOut") {
      // Fade out the word - transitionend event will handle phase change
      setWordOpacity(0);
    } else if (phase === "mSlide") {
      // Show m at original position, then slide to center
      setShowCenteredM(true);
      setWordOpacity(1);
      setMPosition("original");

      // Small delay before sliding to center
      const timeout = setTimeout(() => {
        setMPosition("centered");
        // transitionend event will handle phase change
      }, 200);
      timeouts.push(timeout);
    } else if (phase === "mFinal") {
      // Show sukun on centered m
      const timeout = setTimeout(() => {
        setShowSukunOnM(true);
        // After showing sukun, reset and loop
        setTimeout(() => {
          setPhase("reset");
        }, 500);
      }, 400);
      timeouts.push(timeout);
    } else if (phase === "reset") {
      // Brief pause before restarting
      const timeout = setTimeout(() => {
        setPhase("letters");
      }, 500);
      timeouts.push(timeout);
    }

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [phase]);

  // Position for lone 'm' — absolute inside the hero text layer (not viewport-fixed)
  const getMPositionStyle = () => {
    if (mPosition === "centered") {
      return {
        position: "absolute" as const,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        transition: "transform 1s ease-in-out, left 1s ease-in-out",
      };
    }
    // Original position — approximate 'm' in the word "mirsal" (first letter, left of center)
    return {
      position: "absolute" as const,
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%) translateX(clamp(-8rem, -12vw, -2rem))",
      transition: "transform 1s ease-in-out, left 1s ease-in-out",
    };
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[50vh] flex items-center justify-center pointer-events-none select-none"
      style={{ touchAction: 'pan-y', WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      {/* Full word "mirsal" */}
      {showWord && (
        <div
          ref={wordRef}
          className="flex items-center justify-center mirsal-font mirsal-text-size text-white tracking-wide mirsal-word-container z-[1] mirsal-word-opacity-transition"
          style={{
            opacity: wordOpacity,
          }}
        >
          {letters.map((letter, index) => (
            <span
              key={`${letter}-${index}-${animationKey}`}
              ref={(el) => {
                letterRefs.current[index] = el;
              }}
              className={`relative inline-block mirsal-letter mirsal-letter-${index}`}
            >
              {letter}
              {/* Sukun between 'r' and 's' — SVG so size tracks the letter (em), not glyph metrics */}
              {letter === "r" && showDiacritics && (
                <svg
                  className="absolute mirsal-sukun-after-r"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="28"
                    fill="none"
                    stroke="#33FF00"
                    strokeWidth="14"
                  />
                </svg>
              )}
              {/* Kasra below 'm' (index 0) */}
              {letter === "m" && showDiacritics && (
                <svg
                  className="absolute mirsal-kasra-below-m"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <line
                    x1="20"
                    y1="60"
                    x2="80"
                    y2="40"
                    stroke="#33FF00"
                    strokeWidth="8"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Centered 'm' with sukun - appears after word fades */}
      {showCenteredM && (
        <div
          ref={mRef}
          className="mirsal-font mirsal-text-size text-white tracking-normal z-10"
          style={{
            ...getMPositionStyle(),
            opacity: wordOpacity,
          }}
        >
          <span className="relative inline-block">
            m
            {showSukunOnM && (
              <svg
                className="absolute mirsal-sukun-on-m"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="28"
                  fill="none"
                  stroke="#33FF00"
                  strokeWidth="14"
                />
              </svg>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default AnimatedMirsalText;
